import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Input, Typography } from 'antd';
import { UserOutlined, LockOutlined, KeyOutlined, EyeTwoTone, EyeInvisibleOutlined, ArrowLeftOutlined, InfoCircleOutlined, EnvironmentOutlined, SecurityScanOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { getLoginDeviceInfo } from '../utils/deviceInfo';
import RiskVerificationModal from '../components/RiskVerificationModal';
import './RAMLogin.css';

const { Title, Text } = Typography;

const RAMLogin = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [requireVerification, setRequireVerification] = useState(false);
  const [locationInfo, setLocationInfo] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendCaptcha, setResendCaptcha] = useState({
    captchaId: '',
    captchaSvg: '',
    expiresIn: 0
  });
  const [isMobile, setIsMobile] = useState(false);
  // 新的风险评估相关状态
  const [riskVerificationVisible, setRiskVerificationVisible] = useState(false);
  const [riskData, setRiskData] = useState(null);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 重发验证码倒计时
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // 获取重发用的图形验证码
  const getResendCaptcha = async () => {
    try {
      const response = await API.get('/api/register/captcha');
      setResendCaptcha({
        captchaId: response.data.captchaId,
        captchaSvg: response.data.captchaSvg,
        expiresIn: response.data.expiresIn
      });
    } catch (error) {
      console.error('获取验证码失败:', error);
      setMessage({
        text: '获取图形验证码失败，请稍后再试',
        type: 'error'
      });
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      // 获取设备信息
      const deviceInfo = getLoginDeviceInfo();
      
      const loginData = {
        ...values,
        accountType: 'ram', // 明确标识为RAM账号登录
        deviceId: deviceInfo.deviceId,
        deviceInfo: deviceInfo
      };
      
      const response = await API.post('/api/auth/ram-login', loginData);
      
      if (response.data.requireVerification) {
        // 使用新的风险评估系统
        if (response.data.riskAssessment) {
          // 新的风险评估流程
          setRiskData({
            message: response.data.message,
            currentLocation: response.data.currentLocation,
            lastLocation: response.data.lastLocation,
            riskAssessment: response.data.riskAssessment
          });
          setPendingLoginData(values);
          setRiskVerificationVisible(true);
        } else {
          // 兼容旧的验证流程
          setRequireVerification(true);
          setLocationInfo({
            current: response.data.currentLocation,
            last: response.data.lastLocation
          });
          setMessage({
            text: response.data.message,
            type: 'warning'
          });
          // 立即开始60秒倒计时，因为系统已经自动发送了验证码
          setResendCooldown(60);
          
          // 获取重新发送验证码用的图形验证码
          getResendCaptcha();
        }
      } else {
        // 登录成功
        await handleLoginSuccess(response.data);
      }
    } catch (error) {
      setMessage({
        text: error.response?.data?.message || '登录失败，请重试',
        type: 'error'
      });
    }
    setLoading(false);
  };

  // 统一处理登录成功逻辑
  const handleLoginSuccess = async (responseData) => {
    localStorage.setItem('token', responseData.token);
    localStorage.setItem('role', responseData.role);
    localStorage.setItem('accountType', responseData.accountType || 'ram');
    localStorage.setItem('permissions', JSON.stringify(responseData.permissions));
    localStorage.setItem('parentUsername', responseData.parentUsername);
    
    setMessage({
      text: responseData.message || 'RAM账号登录成功！',
      type: 'success'
    });
    
    // 延迟跳转到仪表板
    setTimeout(() => {
      navigate('/user/dashboard');
    }, 1000);
  };

  // 处理风险验证成功
  const handleRiskVerificationSuccess = async (verificationCode) => {
    try {
      // 获取设备信息
      const deviceInfo = getLoginDeviceInfo();
      
      const loginData = {
        ...pendingLoginData,
        verificationCode: verificationCode,
        accountType: 'ram',
        deviceId: deviceInfo.deviceId,
        deviceInfo: deviceInfo
      };

      const response = await API.post('/api/auth/ram-login', loginData);

      if (response.data.status === 'disabled') {
        throw new Error('账号已被封禁，请联系管理员');
      }

      // 验证成功，关闭弹窗并完成登录
      setRiskVerificationVisible(false);
      await handleLoginSuccess(response.data);

    } catch (err) {
      console.error('验证失败:', err);
      throw new Error(err.response?.data?.message || '验证失败，请重试');
    }
  };

  // 关闭风险验证弹窗
  const handleCloseRiskVerification = () => {
    setRiskVerificationVisible(false);
    setRiskData(null);
    setPendingLoginData(null);
  };

  const handleResendCode = async (captchaCode) => {
    if (resendCooldown > 0) return; // 倒计时期间不允许重发

    if (!captchaCode) {
      setMessage({
        text: '请输入图形验证码',
        type: 'error'
      });
      return;
    }

    setResendLoading(true);
    try {
      const username = form.getFieldValue('username');
      await API.post('/api/auth/resend-login-verification', { 
        username,
        captchaId: resendCaptcha.captchaId,
        captchaCode: captchaCode
      });
      setMessage({
        text: '验证码已重新发送到您的邮箱',
        type: 'success'
      });
      setResendCooldown(60); // 重发成功后重新开始60秒倒计时
      // 清除图形验证码字段并刷新验证码
      form.setFieldsValue({ resendCaptchaCode: '' });
      getResendCaptcha();
    } catch (error) {
      let errorMessage = error.response?.data?.message || '发送失败，请重试';
      
      // 如果错误信息包含验证码相关内容，提供更明确的提示
      if (errorMessage.includes('验证码') || errorMessage.includes('captcha')) {
        errorMessage = '图形验证码错误，请重新输入';
      }
      
      setMessage({
        text: errorMessage,
        type: 'error'
      });
      
      // 刷新图形验证码并清除输入
      getResendCaptcha();
      form.setFieldsValue({ resendCaptchaCode: '' });
    }
    setResendLoading(false);
  };

  const handleBackToMainLogin = () => {
    navigate('/login');
  };

  const handleBackToRAMLogin = () => {
    setRequireVerification(false);
    setLocationInfo(null);
    setMessage({ text: '', type: '' });
    setResendCooldown(0);
    form.resetFields(['verificationCode', 'resendCaptchaCode']);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f0f2f5',
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'center',
      padding: isMobile ? '10px' : '20px',
      paddingTop: isMobile ? '20px' : '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '100%' : '1000px',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '20px',
          alignItems: 'stretch'
        }}>
          {/* 左侧介绍区域 */}
          <Card 
            style={{ 
              flex: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              order: isMobile ? 1 : 1
            }}
          >
            <div style={{ padding: isMobile ? '16px' : '20px', textAlign: 'center' }}>
              <div style={{ marginBottom: isMobile ? '20px' : '32px' }}>
                <SecurityScanOutlined style={{ 
                  fontSize: isMobile ? '36px' : '48px', 
                  color: '#1890ff', 
                  marginBottom: isMobile ? '12px' : '16px' 
                }} />
              </div>
              
              <Title level={isMobile ? 3 : 2} style={{ marginBottom: isMobile ? '12px' : '16px' }}>
                企业子账号管理系统
              </Title>
              
              <Text style={{ 
                fontSize: isMobile ? '14px' : '16px', 
                color: '#666', 
                display: 'block', 
                marginBottom: isMobile ? '20px' : '32px' 
              }}>
                Resource Access Management Portal
              </Text>
              
              {/* 根据验证状态显示不同内容 */}
              {!requireVerification ? (
                // 普通登录状态 - 显示登录须知
                <div className="ram-info" style={{
                  background: '#fafafa',
                  borderRadius: '8px',
                  padding: isMobile ? '16px' : '20px',
                  border: '1px solid #f0f0f0'
                }}>
                  <div className="ram-info-title" style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: isMobile ? '12px' : '16px',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: 600,
                    color: '#262626'
                  }}>
                    <InfoCircleOutlined style={{ 
                      color: '#1890ff', 
                      marginRight: '8px',
                      fontSize: isMobile ? '14px' : '16px'
                    }} />
                    <span>企业子账号登录须知</span>
                  </div>
                  <div className="ram-info-content" style={{
                    fontSize: isMobile ? '12px' : '13px',
                    lineHeight: '1.6',
                    color: '#666'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      企业子账号由主账号管理员统一创建和管理
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      账号权限由主账号管理员根据职责分配
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      仅可访问已授权的功能模块和数据资源
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      如需密码重置请联系主账号管理员处理
                    </div>
                    <div style={{ marginBottom: 0 }}>
                      异地登录将触发邮箱验证以确保账号安全
                    </div>
                  </div>
                </div>
              ) : (
                // 验证状态 - 显示安全验证说明
                <div>
                  {locationInfo && (
                    <div style={{
                      background: '#fff7e6',
                      borderRadius: '8px',
                      padding: '16px',
                      border: '1px solid #ffd591',
                      marginBottom: '20px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        marginBottom: '12px',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#fa8c16'
                      }}>
                        <EnvironmentOutlined style={{ marginRight: '8px' }} />
                        登录位置信息
                      </div>
                                             <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.6', textAlign: 'left' }}>
                         <div style={{ textAlign: 'left' }}><strong>当前登录位置：</strong>{locationInfo.current}</div>
                         <div style={{ textAlign: 'left' }}><strong>上次登录位置：</strong>{locationInfo.last}</div>
                       </div>
                    </div>
                  )}
                  
                  <div className="ram-info" style={{
                    background: '#f6ffed',
                    borderRadius: '8px',
                    padding: '20px',
                    border: '1px solid #b7eb8f'
                  }}>
                    <div className="ram-info-title" style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '16px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#389e0d'
                    }}>
                      <SecurityScanOutlined style={{ 
                        color: '#52c41a', 
                        marginRight: '8px',
                        fontSize: '16px'
                      }} />
                      <span>安全验证流程</span>
                    </div>
                    <div className="ram-info-content" style={{
                      fontSize: '13px',
                      lineHeight: '1.6',
                      color: '#666'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        1. 系统已向您的邮箱发送6位安全验证码
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        2. 验证码有效期为10分钟，请及时输入
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        3. 如未收到邮件，请检查垃圾邮箱
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        4. 可通过图形验证码重新发送邮件验证码
                      </div>
                      <div style={{ marginBottom: 0 }}>
                        5. 验证成功后即可正常访问企业系统
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* 右侧登录表单区域 */}
          <Card 
            style={{ 
              flex: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              order: isMobile ? 2 : 2
            }}
          >
            <div style={{ padding: isMobile ? '16px' : '20px' }}>
              {/* 标题区域 - 验证状态下隐藏 */}
              {!requireVerification && (
                <div className="ram-login-header" style={{ textAlign: 'center', marginBottom: isMobile ? '24px' : '32px' }}>
                  <Title level={isMobile ? 4 : 3} className="ram-login-title" style={{ marginBottom: '8px' }}>
                    登录您的账号
                  </Title>
                  <Text className="ram-login-subtitle" style={{ 
                    color: '#666', 
                    fontSize: isMobile ? '13px' : '14px' 
                  }}>
                    请输入您的企业子账号凭据
                  </Text>
                </div>
              )}

              {requireVerification && (
                <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '24px' }}>
                  <SecurityScanOutlined style={{ 
                    fontSize: isMobile ? '28px' : '32px', 
                    color: '#1890ff', 
                    marginBottom: isMobile ? '8px' : '12px' 
                  }} />
                  <Title level={isMobile ? 4 : 3} style={{ marginBottom: '8px' }}>
                    安全验证
                  </Title>
                  <Text style={{ 
                    color: '#666', 
                    fontSize: isMobile ? '13px' : '14px' 
                  }}>
                    请完成身份验证以继续登录
                  </Text>
                </div>
              )}

              <div className="ram-login-body">
                {/* 消息提示 */}
                {message.text && (
                  <Alert
                    message={message.text}
                    type={message.type}
                    showIcon
                    style={{ marginBottom: '20px' }}
                  />
                )}



                <Form
                  form={form}
                  onFinish={handleSubmit}
                  layout="vertical"
                  className="ram-form"
                  size={isMobile ? "middle" : "large"}
                >
                  {/* 验证状态下隐藏用户名和密码字段 */}
                  {!requireVerification && (
                    <>
                      <Form.Item
                        name="username"
                        label={
                          <span style={{ fontWeight: 600, color: '#262626' }}>
                            企业子账号
                          </span>
                        }
                        rules={[{ required: true, message: '请输入企业子账号' }]}
                      >
                        <Input
                          prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                          placeholder="username@company.com"
                          style={{ 
                            borderRadius: '6px',
                            fontSize: isMobile ? '16px' : '14px',
                            height: isMobile ? '44px' : 'auto'
                          }}
                        />
                      </Form.Item>

                      <Form.Item
                        name="password"
                        label={
                          <span style={{ fontWeight: 600, color: '#262626' }}>
                            登录密码
                          </span>
                        }
                        rules={[{ required: true, message: '请输入登录密码' }]}
                      >
                        <Input.Password
                          prefix={<LockOutlined style={{ color: '#1890ff' }} />}
                          placeholder="请输入您的登录密码"
                          size={isMobile ? "middle" : "large"}
                          style={{ 
                            borderRadius: '6px',
                            fontSize: isMobile ? '16px' : '14px',
                            height: isMobile ? '44px' : 'auto'
                          }}
                          iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        />
                      </Form.Item>
                    </>
                  )}

                  {/* 验证状态下保留用户名和密码作为隐藏字段 */}
                  {requireVerification && (
                    <>
                      <Form.Item
                        name="username"
                        style={{ display: 'none' }}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name="password"
                        style={{ display: 'none' }}
                      >
                        <Input />
                      </Form.Item>
                    </>
                  )}

                  {/* 验证码输入框 */}
                  {requireVerification && (
                    <>
                      <Form.Item
                        name="verificationCode"
                        label={
                          <span style={{ fontWeight: 600, color: '#262626' }}>
                            <SecurityScanOutlined style={{ marginRight: '4px', color: '#1890ff' }} />
                            安全验证码
                          </span>
                        }
                        rules={[{ required: true, message: '请输入安全验证码' }]}
                      >
                        <Input
                          prefix={<KeyOutlined style={{ color: '#1890ff' }} />}
                          placeholder="请输入6位安全验证码"
                          size={isMobile ? "middle" : "large"}
                          maxLength={6}
                          style={{ 
                            borderRadius: '6px',
                            fontSize: isMobile ? '16px' : '14px',
                            letterSpacing: '2px',
                            textAlign: 'center',
                            height: isMobile ? '44px' : 'auto'
                          }}
                        />
                      </Form.Item>

                      {/* 重新发送验证码区域 */}
                      <div style={{
                        background: '#fafafa',
                        borderRadius: '6px',
                        padding: isMobile ? '12px' : '16px',
                        marginBottom: isMobile ? '16px' : '20px',
                        border: '1px solid #e8e8e8'
                      }}>
                        <div style={{ 
                          marginBottom: isMobile ? '10px' : '12px',
                          fontSize: isMobile ? '12px' : '13px',
                          color: '#666',
                          textAlign: 'center'
                        }}>
                          没有收到验证码？请输入图形验证码后重新发送
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: isMobile ? '6px' : '8px', 
                          marginBottom: isMobile ? '10px' : '12px',
                          flexDirection: isMobile ? 'column' : 'row'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px',
                            width: isMobile ? '100%' : 'auto'
                          }}>
                            <div 
                              style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                width: isMobile ? '80px' : '100px',
                                height: isMobile ? '32px' : '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#fff'
                              }}
                              dangerouslySetInnerHTML={{ __html: resendCaptcha.captchaSvg }}
                              onClick={getResendCaptcha}
                              title="点击刷新验证码"
                            />
                            <Form.Item 
                              name="resendCaptchaCode" 
                              style={{ margin: 0, flex: 1, minWidth: isMobile ? '80px' : '100px' }}
                              rules={[{ required: false }]}
                            >
                              <Input 
                                placeholder="图形验证码"
                                maxLength={4}
                                style={{ 
                                  textTransform: 'uppercase',
                                  height: isMobile ? '32px' : '36px',
                                  fontSize: isMobile ? '14px' : '16px'
                                }}
                              />
                            </Form.Item>
                            <Button 
                              icon={<SecurityScanOutlined />} 
                              onClick={getResendCaptcha}
                              title="刷新"
                              size={isMobile ? 'small' : 'middle'}
                            />
                          </div>
                        </div>
                        
                        <Button 
                          type="primary"
                          ghost
                          onClick={() => handleResendCode(form.getFieldValue('resendCaptchaCode'))}
                          loading={resendLoading}
                          disabled={resendCooldown > 0}
                          block
                          size={isMobile ? 'middle' : 'large'}
                          style={{ 
                            borderRadius: '6px',
                            height: isMobile ? '36px' : 'auto'
                          }}
                        >
                          {resendCooldown > 0 ? `${resendCooldown}秒后重发` : '重新发送验证码'}
                        </Button>
                      </div>
                    </>
                  )}

                  <Form.Item style={{ marginTop: requireVerification ? (isMobile ? '16px' : '20px') : (isMobile ? '24px' : '32px') }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      size={isMobile ? "middle" : "large"}
                      block
                      className="ram-submit-button"
                      icon={<SecurityScanOutlined />}
                      style={{ 
                        borderRadius: '8px',
                        height: isMobile ? '44px' : '48px',
                        fontSize: isMobile ? '15px' : '16px',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                      }}
                    >
                      {requireVerification ? '安全验证登录' : '企业账号登录'}
                    </Button>
                  </Form.Item>
                </Form>



                {/* 返回主账号登录按钮 */}
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: isMobile ? '20px' : '24px',
                  paddingTop: isMobile ? '20px' : '24px',
                  borderTop: '1px solid #f0f0f0'
                }}>
                  <Button
                    type="link"
                    onClick={requireVerification ? handleBackToRAMLogin : handleBackToMainLogin}
                    style={{ 
                      color: '#666',
                      fontSize: isMobile ? '13px' : '14px',
                      padding: 0
                    }}
                    icon={<ArrowLeftOutlined />}
                  >
                    {requireVerification ? '返回' : '返回主账号登录'}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* 风险验证弹窗 */}
      <RiskVerificationModal
        visible={riskVerificationVisible}
        onClose={handleCloseRiskVerification}
        onVerificationSuccess={handleRiskVerificationSuccess}
        riskData={riskData}
        pendingLoginData={pendingLoginData}
        isMobile={isMobile}
      />
    </div>
  );
};

export default RAMLogin; 